import { icons } from '../assets/icons';
import { NewsService, NewsPost } from '../services/news';

export class NewsHubComponent {
  private searchQuery: string = '';
  private activeTag: string = 'All';

  public render(): string {
    const featured = NewsService.getFeaturedPost();
    const allTags = [{ tag: 'All', count: NewsService.getPosts().length }, ...NewsService.getAllTags()];
    const filteredPosts = NewsService.filterPosts(this.searchQuery, this.activeTag);

    return `
      <div id="news-hub-container" class="min-h-screen pt-4 pb-20 font-mono text-left">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <!-- Header Hero -->
          <div class="text-center max-w-3xl mx-auto space-y-4 pt-4">
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-matrix-950/80 text-matrix-400 border border-matrix-800 text-xs font-semibold shadow-glow-sm">
              ${icons.terminal('w-3.5 h-3.5')}
              <span>Project Updates, Tutorials & Release Notes</span>
            </div>
            
            <h1 class="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Mastui News & Blog
            </h1>
            
            <p class="text-xs sm:text-base text-terminal-muted leading-relaxed max-w-2xl mx-auto">
              Stay up to date with the latest Mastui releases, terminal graphics tutorials, filter recipes, and Fediverse developments.
            </p>

            <div class="flex items-center justify-center gap-3 pt-2">
              <a 
                href="/feed.xml" 
                target="_blank"
                class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-amber-400 bg-amber-950/60 hover:bg-amber-900/80 border border-amber-800/80 transition-all shadow-sm"
                title="Subscribe to RSS Feed"
              >
                ${icons.zap('w-3.5 h-3.5 text-amber-400')}
                <span>Subscribe via RSS (/feed.xml)</span>
              </a>
            </div>
          </div>

          <!-- Featured Post Hero Banner (if exists and on "All" / no search) -->
          ${featured && !this.searchQuery && this.activeTag === 'All' ? `
            <div class="rounded-3xl bg-terminal-card border border-matrix-500/40 p-6 sm:p-8 lg:p-10 shadow-glow-lg relative overflow-hidden group">
              <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                
                ${featured.coverImage ? `
                  <div class="lg:col-span-5 rounded-2xl overflow-hidden border border-matrix-900 bg-black max-h-[280px]">
                    <img 
                      src="${featured.coverImage}" 
                      alt="${featured.title}" 
                      class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ` : ''}

                <div class="${featured.coverImage ? 'lg:col-span-7' : 'lg:col-span-12'} space-y-4">
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="px-2.5 py-0.5 rounded text-[11px] font-bold bg-matrix-500 text-terminal-bgDark shadow-glow-sm">
                      ★ FEATURED
                    </span>
                    ${featured.tags.map(t => `
                      <span class="px-2 py-0.5 rounded text-[11px] font-medium bg-matrix-950 text-matrix-400 border border-matrix-800">
                        #${t}
                      </span>
                    `).join('')}
                    <span class="text-xs text-terminal-muted">•</span>
                    <span class="text-xs text-terminal-muted">${featured.date}</span>
                    <span class="text-xs text-terminal-muted">•</span>
                    <span class="text-xs text-cyber-400">${featured.readingTime}</span>
                  </div>

                  <h2 class="text-xl sm:text-3xl font-extrabold text-white group-hover:text-matrix-400 transition-colors">
                    <a href="#/news/${featured.slug}">${featured.title}</a>
                  </h2>

                  <p class="text-xs sm:text-sm text-terminal-fg/90 leading-relaxed max-w-3xl">
                    ${featured.summary}
                  </p>

                  <div class="flex items-center justify-between pt-3 border-t border-terminal-border/60">
                    <div class="flex items-center gap-2.5">
                      ${featured.authorAvatar ? `
                        <img src="${featured.authorAvatar}" alt="${featured.author}" class="w-7 h-7 rounded-full border border-matrix-500/50 object-cover" />
                      ` : ''}
                      <span class="text-xs text-terminal-muted font-medium">${featured.author}</span>
                    </div>

                    <a 
                      href="#/news/${featured.slug}"
                      class="inline-flex items-center gap-1 text-xs font-bold text-matrix-400 group-hover:text-matrix-300 group-hover:underline"
                    >
                      <span>Read Full Story</span>
                      ${icons.arrowRight('w-3.5 h-3.5')}
                    </a>
                  </div>
                </div>

              </div>
            </div>
          ` : ''}

          <!-- Search & Tag Filter Bar -->
          <div class="space-y-4 pt-2">
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              
              <!-- Search Input -->
              <div class="relative flex-1 max-w-md">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-matrix-600">
                  ${icons.search('w-4 h-4')}
                </div>
                <input 
                  type="text" 
                  id="news-search-input"
                  placeholder="Search articles by keyword or topic..."
                  value="${this.searchQuery}"
                  class="w-full pl-9 pr-4 py-2.5 text-xs rounded-xl bg-terminal-card border border-terminal-border text-terminal-fg placeholder-terminal-muted focus:outline-none focus:border-matrix-500 focus:shadow-glow-sm transition-all"
                />
              </div>

              <!-- Posts Counter -->
              <div class="text-xs text-terminal-muted self-end sm:self-center">
                Showing <strong class="text-matrix-400">${filteredPosts.length}</strong> ${filteredPosts.length === 1 ? 'article' : 'articles'}
              </div>

            </div>

            <!-- Tag Pills -->
            <div class="flex flex-wrap items-center gap-2 pt-1">
              ${allTags.map(({ tag, count }) => `
                <button 
                  data-news-tag="${tag}"
                  class="news-tag-btn px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    this.activeTag.toLowerCase() === tag.toLowerCase()
                      ? 'bg-matrix-500 text-terminal-bgDark shadow-glow'
                      : 'bg-terminal-card text-terminal-muted hover:text-terminal-fg border border-terminal-border hover:border-matrix-500/50'
                  }"
                >
                  <span>${tag}</span>
                  <span class="ml-1 opacity-70 text-[10px]">(${count})</span>
                </button>
              `).join('')}
            </div>
          </div>

          <!-- Articles Grid -->
          <div id="news-posts-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            ${this.renderArticlesGrid(filteredPosts)}
          </div>

        </div>
      </div>
    `;
  }

  private renderArticlesGrid(posts: NewsPost[]): string {
    if (posts.length === 0) {
      return `
        <div class="col-span-full py-16 text-center space-y-3 bg-terminal-card rounded-2xl border border-terminal-border">
          <div class="w-10 h-10 rounded-full bg-matrix-950 text-matrix-500 flex items-center justify-center mx-auto">
            ${icons.search('w-5 h-5')}
          </div>
          <h3 class="text-base font-bold text-white">No articles matched your criteria</h3>
          <p class="text-xs text-terminal-muted max-w-sm mx-auto">
            Try adjusting your search query or switching to "All" tags.
          </p>
          <button 
            id="news-reset-filter-btn"
            class="px-4 py-2 rounded-xl text-xs font-bold bg-matrix-500 text-terminal-bgDark hover:bg-matrix-400 transition-colors cursor-pointer"
          >
            Reset Filters
          </button>
        </div>
      `;
    }

    return posts.map(post => `
      <article class="p-5 rounded-2xl bg-terminal-card border border-terminal-border hover:border-matrix-500/60 shadow-glow-sm hover:shadow-glow flex flex-col justify-between group transition-all">
        
        <div class="space-y-3.5">
          ${post.coverImage ? `
            <div class="rounded-xl overflow-hidden border border-matrix-900 bg-black aspect-video">
              <img 
                src="${post.coverImage}" 
                alt="${post.title}" 
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          ` : ''}

          <div class="flex flex-wrap items-center gap-1.5">
            ${post.tags.slice(0, 2).map(t => `
              <span class="px-2 py-0.5 rounded text-[10px] font-semibold bg-matrix-950 text-matrix-400 border border-matrix-800">
                #${t}
              </span>
            `).join('')}
            <span class="text-[11px] text-terminal-muted ml-auto">${post.readingTime}</span>
          </div>

          <h3 class="font-bold text-white group-hover:text-matrix-400 transition-colors text-base line-clamp-2">
            <a href="#/news/${post.slug}">${post.title}</a>
          </h3>

          <p class="text-xs text-terminal-fg/80 leading-relaxed line-clamp-3">
            ${post.summary}
          </p>
        </div>

        <div class="pt-4 mt-4 border-t border-terminal-border/60 flex items-center justify-between text-xs">
          <div class="flex items-center gap-2">
            ${post.authorAvatar ? `
              <img src="${post.authorAvatar}" alt="${post.author}" class="w-5 h-5 rounded-full border border-matrix-500/40 object-cover" />
            ` : ''}
            <span class="text-[11px] text-terminal-muted truncate max-w-[100px]">${post.author}</span>
            <span class="text-[11px] text-terminal-muted/60">• ${post.date}</span>
          </div>

          <a 
            href="#/news/${post.slug}"
            class="font-bold text-matrix-400 group-hover:text-matrix-300 flex items-center gap-1 whitespace-nowrap"
          >
            <span>Read →</span>
          </a>
        </div>

      </article>
    `).join('');
  }

  public bindEvents() {
    const searchInput = document.getElementById('news-search-input') as HTMLInputElement;
    const tagButtons = document.querySelectorAll('.news-tag-btn');
    const resetBtn = document.getElementById('news-reset-filter-btn');

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.searchQuery = (e.target as HTMLInputElement).value;
        this.updateView();
      });
    }

    tagButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const tag = btn.getAttribute('data-news-tag') || 'All';
        this.activeTag = tag;
        this.updateView();
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.searchQuery = '';
        this.activeTag = 'All';
        this.updateView();
      });
    }
  }

  private updateView() {
    const container = document.getElementById('news-hub-container');
    if (container) {
      container.outerHTML = this.render();
      this.bindEvents();
    }
  }
}
